import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Card,
    Select,
    Skeleton,
    Space,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import { useAuth } from '../../../hooks/useAuth';
import {
    aiAdminApi,
    ModelBeliefsGroup,
    ModelBeliefsStatement,
    ModelBeliefsStatementsResponse,
} from '../../../services/ai-admin-api';
import { TEMPLATE_TYPES } from './templateTypes';

const { Text, Paragraph } = Typography;

const VOICE_HELP: Record<string, string> = {
    subject: 'A judgement about the brand. The model can hold it itself.',
    witness: 'Something only a customer can have — loyalty, belonging, seeing themselves in the brand. The model estimates it and answers null for itself.',
    fact: 'A checkable claim about the world: price, deals, availability, advertising. The stale-claim detector lives here.',
};

const GROUP_HELP: Record<string, string> = {
    A: 'Facts the models can check from the record. Asked both ways, for the brand and its competitors; the only group where the models agree closely enough to compare brands.',
    B: 'Opinions. Asked both ways for the brand and its competitors, shown per model, never ranked across brands.',
    C: 'Feelings only a customer can report. Never asked by the AI read; the page shows them as "only your customers can answer this".',
};

const GROUP_COLOUR: Record<string, string> = { A: 'blue', B: 'green', C: 'gold' };

// The instrument, as a table. Voice and no-referent decide what a statement
// MEANS to the read, not merely how it is scored: voice decides whether a model
// answers as itself at all, and the fact class doubles as the stale-claim
// detector. Both were seeded from the question CATEGORY, which is what lets
// them survive a rewording — this edits the exceptions.
export const ModelBeliefsStatementsPanel: React.FC = () => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';

    const [templateType, setTemplateType] = useState('durable');
    const [data, setData] = useState<ModelBeliefsStatementsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [savingCode, setSavingCode] = useState<string | null>(null);

    const load = useCallback(
        async (type: string) => {
            if (!jwt) return;
            setLoading(true);
            try {
                setData(await aiAdminApi.listModelBeliefsStatements(jwt, type));
            } finally {
                setLoading(false);
            }
        },
        [jwt],
    );

    useEffect(() => {
        void load(templateType);
    }, [load, templateType]);

    const save = async (
        code: string,
        patch: {
            voice?: ModelBeliefsStatement['voice'];
            noReferent?: boolean;
            group?: ModelBeliefsGroup | null;
        },
    ) => {
        setSavingCode(code);
        try {
            const result = await aiAdminApi.saveModelBeliefsStatementMeta(jwt, {
                templateType,
                code,
                ...patch,
            });
            (result.warnings ?? []).forEach((w) => message.warning(w, 8));
            await load(templateType);
        } catch (e) {
            message.error((e as Error).message);
        } finally {
            setSavingCode(null);
        }
    };

    if (loading && !data) return <Skeleton active paragraph={{ rows: 10 }} />;

    const withParaphrases = (data?.statements ?? []).filter((s) => s.paraphrases > 0);

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
                type="info"
                showIcon
                message="One wording per statement, by measurement"
                description="The wording probe (run 31) found fact-voice statements move about four times the noise band when reworded, because the phrasing sets the threshold of the claim — PD-1 reads 3.33 as “on promotion more often than not” against ~5.1 as “frequently on sale”. Subject and witness statements do not move at all. So rotation is safe where it is unnecessary and unsafe where it looks necessary: w0 is pinned, and paraphrases stay empty outside a deliberate probe."
            />

            {withParaphrases.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message={`${withParaphrases.length} statement(s) carry paraphrases`}
                    description={`A read will rotate them: ${withParaphrases
                        .map((s) => s.code)
                        .join(', ')}. That is correct only while a probe is running.`}
                />
            )}

            <Card
                size="small"
                title="Statements"
                extra={
                    <Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Template type
                        </Text>
                        <Select
                            size="small"
                            style={{ width: 170 }}
                            value={templateType}
                            options={TEMPLATE_TYPES.map((t) => ({ value: t, label: t }))}
                            onChange={(value: string) => setTemplateType(value)}
                        />
                    </Space>
                }
            >
                <Table
                    rowKey="code"
                    size="small"
                    pagination={{ pageSize: 30, hideOnSinglePage: true }}
                    dataSource={data?.statements ?? []}
                    columns={[
                        { title: 'Code', dataIndex: 'code', width: 90 },
                        { title: 'Category', dataIndex: 'category', width: 190 },
                        {
                            title: (
                                <Tooltip title="Adir's v3 groups. A and B are asked by the AI read; C is left to the customer survey. Unseeded statements are never asked.">
                                    <span>Group</span>
                                </Tooltip>
                            ),
                            dataIndex: 'group',
                            width: 130,
                            render: (group: ModelBeliefsGroup | null, row: ModelBeliefsStatement) => (
                                <Space size={4}>
                                    <Tooltip title={group ? GROUP_HELP[group] : 'Unseeded: not asked.'}>
                                        <Select
                                            size="small"
                                            style={{ width: 70 }}
                                            value={group ?? undefined}
                                            placeholder="—"
                                            allowClear
                                            loading={savingCode === row.code}
                                            options={[
                                                { value: 'A', label: <Tag color={GROUP_COLOUR.A}>A</Tag> },
                                                { value: 'B', label: <Tag color={GROUP_COLOUR.B}>B</Tag> },
                                                { value: 'C', label: <Tag color={GROUP_COLOUR.C}>C</Tag> },
                                            ]}
                                            onChange={(next?: ModelBeliefsGroup) =>
                                                save(row.code, { group: next ?? null })
                                            }
                                        />
                                    </Tooltip>
                                    {row.selfFromRank && (
                                        <Tooltip title="The AI view of this statement is scored from the rank question (named first = 7 … not in the six = 1), not asked as a statement.">
                                            <Tag>rank</Tag>
                                        </Tooltip>
                                    )}
                                </Space>
                            ),
                        },
                        {
                            title: 'Statement',
                            key: 'text',
                            render: (_: unknown, row: ModelBeliefsStatement) => (
                                <Text style={{ fontSize: 13 }}>
                                    {row.wordings?.[0]?.text ?? '—'}
                                </Text>
                            ),
                        },
                        {
                            title: 'Voice',
                            dataIndex: 'voice',
                            width: 130,
                            render: (voice: ModelBeliefsStatement['voice'], row) => (
                                <Tooltip title={VOICE_HELP[voice]}>
                                    <Select
                                        size="small"
                                        style={{ width: 110 }}
                                        value={voice}
                                        loading={savingCode === row.code}
                                        options={[
                                            { value: 'subject', label: 'subject' },
                                            { value: 'witness', label: 'witness' },
                                            { value: 'fact', label: 'fact' },
                                        ]}
                                        onChange={(next: ModelBeliefsStatement['voice']) =>
                                            save(row.code, { voice: next })
                                        }
                                    />
                                </Tooltip>
                            ),
                        },
                        {
                            title: (
                                <Tooltip title="The model cannot hold this itself — loyalty, belonging, seeing yourself in a brand. Its own answer is recorded as absent rather than as a low number, and the customer estimate is the measurement.">
                                    <span>No referent</span>
                                </Tooltip>
                            ),
                            dataIndex: 'noReferent',
                            width: 110,
                            render: (noReferent: boolean, row: ModelBeliefsStatement) => (
                                <Switch
                                    size="small"
                                    checked={noReferent}
                                    loading={savingCode === row.code}
                                    onChange={(checked) =>
                                        save(row.code, { noReferent: checked })
                                    }
                                />
                            ),
                        },
                        {
                            title: 'Scale',
                            key: 'scale',
                            width: 110,
                            render: (_: unknown, row: ModelBeliefsStatement) => (
                                <Space size={4}>
                                    <Text style={{ fontSize: 12 }}>{row.scale}</Text>
                                    {row.allowsNa && <Tag>N/A</Tag>}
                                </Space>
                            ),
                        },
                        {
                            title: 'Wordings',
                            dataIndex: 'paraphrases',
                            width: 110,
                            render: (count: number, row: ModelBeliefsStatement) =>
                                count > 0 ? (
                                    <Tag color={row.voice === 'fact' ? 'red' : 'orange'}>
                                        w0 + {count}
                                    </Tag>
                                ) : (
                                    <Text type="secondary">w0 only</Text>
                                ),
                        },
                    ]}
                />
                <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
                    Wording is per template type: 3p_durable rewords 19 of 57 codes and
                    sports_betting 14, so the same code is a different sentence — and, for a fact
                    statement, a different measurement — across types.
                </Paragraph>
            </Card>
        </Space>
    );
};
